/**
 * CompanyLogo — three-tier logo ladder for company suggestions / accounts.
 *
 *   1. Clearbit Logo API   (free, real branded logos)
 *   2. Google s2 favicon   (fallback when Clearbit returns nothing)
 *   3. Initial pill        (final fallback — coloured by domain hash)
 *
 * Usage: <CompanyLogo domain="cleerio.ai" name="Cleerio.ai" size={36} />
 */
import { useState } from 'react';

const TONES = [
  'bg-rose-100 text-rose-700 dark:bg-rose-900/30 dark:text-rose-300',
  'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300',
  'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300',
  'bg-sky-100 text-sky-700 dark:bg-sky-900/30 dark:text-sky-300',
  'bg-indigo-100 text-indigo-700 dark:bg-indigo-900/30 dark:text-indigo-300',
  'bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-300',
  'bg-slate-200 text-slate-700 dark:bg-slate-700 dark:text-slate-200',
];

function pickTone(seed = '') {
  let h = 0;
  for (let i = 0; i < seed.length; i++) h = (h << 5) - h + seed.charCodeAt(i);
  return TONES[Math.abs(h) % TONES.length];
}

export default function CompanyLogo({ domain, name, size = 36, rounded = 'rounded-lg', className = '' }) {
  // Stage 0 = Clearbit, 1 = Google favicon, 2 = initial pill
  const [stage, setStage] = useState(0);
  const initial = (name || domain || '?').replace(/^https?:\/\//, '').trim().slice(0, 1).toUpperCase();
  const sizePx = `${size}px`;

  if (!domain) {
    return (
      <span
        className={`inline-flex items-center justify-center font-semibold ${rounded} ${pickTone(name)} ${className}`}
        style={{ width: sizePx, height: sizePx, fontSize: Math.round(size * 0.42) }}
      >
        {initial}
      </span>
    );
  }

  const cleanDomain = domain.replace(/^https?:\/\//, '').replace(/\/$/, '');

  if (stage < 2) {
    const src = stage === 0
      ? `https://logo.clearbit.com/${cleanDomain}?size=${size * 2}`
      : `https://www.google.com/s2/favicons?domain=${cleanDomain}&sz=${Math.max(32, size * 2)}`;
    return (
      <img
        src={src}
        alt={name || cleanDomain}
        width={size}
        height={size}
        loading="lazy"
        onError={() => setStage((s) => s + 1)}
        className={`object-contain bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 ${rounded} ${className}`}
        style={{ width: sizePx, height: sizePx }}
      />
    );
  }

  return (
    <span
      className={`inline-flex items-center justify-center font-semibold ${rounded} ${pickTone(cleanDomain)} ${className}`}
      style={{ width: sizePx, height: sizePx, fontSize: Math.round(size * 0.42) }}
    >
      {initial}
    </span>
  );
}
